import type { DayPoint, LandingMetrics, PrizeCount } from "@/lib/types";

const fmt = (n: number) => n.toLocaleString("ru-RU");

function shortDate(iso: string): string {
  // iso is "YYYY-MM-DD"
  const [, m, d] = iso.split("-");
  return `${d}.${m}`;
}

/** Daily bar chart: visitors per day with prizes marked, dates on the axis. */
export function DailyChart({ data }: { data: DayPoint[] }) {
  if (data.length === 0) return <div className="empty-note">Нет данных по дням за период.</div>;
  const peak = Math.max(1, ...data.map((d) => d.visitors));
  return (
    <div className="daily">
      {data.map((d) => (
        <div className="dcol" key={d.date} title={`${d.date}\n${d.visitors} посетителей\n${d.game_starts} сыграли\n${d.prizes_won} призов`}>
          <div className="dbarwrap">
            <div className="dbar" style={{ height: `${Math.round((d.visitors / peak) * 100)}%` }}>
              {d.prizes_won > 0 && (
                <div
                  className="dbar-prize"
                  style={{ height: `${Math.round((d.prizes_won / Math.max(d.visitors, 1)) * 100)}%` }}
                />
              )}
            </div>
          </div>
          <div className="dval">{fmt(d.visitors)}</div>
          <div className="dlabel">{shortDate(d.date)}</div>
        </div>
      ))}
    </div>
  );
}

/** Won-prizes breakdown: label, count, share, proportional bar. */
export function PrizeList({ prizes }: { prizes: PrizeCount[] }) {
  if (prizes.length === 0)
    return <div className="empty-note">Пока никто не выиграл приз за этот период.</div>;
  const total = prizes.reduce((s, p) => s + p.count, 0);
  const peak = Math.max(1, ...prizes.map((p) => p.count));
  return (
    <div className="prizes">
      {prizes.map((p) => (
        <div className="prow" key={p.prize_id}>
          <div className="pname">🎁 {p.label}</div>
          <div className="ptrack">
            <div className="pfill" style={{ width: `${Math.round((p.count / peak) * 100)}%` }} />
          </div>
          <div className="pcount">
            <b>{fmt(p.count)}</b>
            <span className="muted small"> · {Math.round((p.count / total) * 100)}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Conversion funnel: visitors → played → finished → won, with drop-off percents. */
export function Funnel({ m }: { m: LandingMetrics }) {
  const steps = [
    { label: "Зашли", value: m.visitors },
    { label: "Начали играть", value: m.game_starts },
    { label: "Доиграли", value: m.game_finishes },
    { label: "Выиграли приз", value: m.prizes_won },
  ];
  const top = Math.max(1, m.visitors);
  return (
    <div className="funnel">
      {steps.map((s, i) => (
        <div className="fstep" key={s.label}>
          <div className="ftrack">
            <div className="ffill" style={{ width: `${Math.round((s.value / top) * 100)}%` }}>
              <span className="fnum">{fmt(s.value)}</span>
            </div>
          </div>
          <div className="flabel">
            {s.label}
            {i > 0 && steps[i - 1].value > 0 && (
              <span className="muted small"> · {Math.round((s.value / steps[i - 1].value) * 100)}%</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
